import Price from '@/components/Price';
import Rating from '@/components/Rating';
import { NEXT_IMAGE_PLACEHOLDER } from '@/constants/next-image-placeholder';
import { SEO_CATEGORY_MAP } from '@/constants/seoCategoryMap';
import { useGalleryItem } from '@/hooks/Gallery/useGalleryItem';
import useIsMobile from '@/hooks/use-is-mobile';
import { useGetCategoryFinalPriceAndCrossPrice } from '@/hooks/useGetCategoryPriceAndCrossPrice';
import { classnameMerge } from '@/util/classname-merge';
import { transformColorLabelToPathname } from '@/util/transform-color-label-to-pathname';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import CustomizeOptions from './CustomizeOptions';

const GalleryItem = props => {
  const {
    hideMark = false,
    showReview = true,
    hideProductName = false,
    freeShipping = false,
    inCategoryPage = false,
    showRating = true,
    categoryName,
    categoryId,
    classes = {},
  } = props;

  const pathname = usePathname();

  const {
    handleLinkClick,
    item,
    colorOptions,
    allColorCount,
    versionedBaseImage,
    versionedHoverImage,
    selectedColor,
    setSelectedShowColorIndex,
    showCustomizeOptions = true,
    selectedShowColorIndex,
  } = useGalleryItem(props);
  const { url_key } = item;

  const isQueryProductInStock = pathname.includes('product-stock');

  const productLink = `/${url_key}.html${isQueryProductInStock ? '?in-stock=true' : ''}`;

  const generateLinkByColorLabel = (colorLabel, isChangeUrl) => {
    if (isQueryProductInStock || !inCategoryPage || !colorLabel || !SEO_CATEGORY_MAP[categoryId] || !isChangeUrl) {
      return productLink;
    }

    return productLink.replace(/^([^-]*-[^-]*)/, `$1-${transformColorLabelToPathname(colorLabel)}`);
  };

  const renderProductLink =
    selectedColor && selectedColor.label
      ? generateLinkByColorLabel(selectedColor.label, selectedColor.is_change_url)
      : productLink;

  const isMobile = useIsMobile();

  const { name, rating_summary, ampProtOption } = item;
  const renderProductName = name?.replace(/&quot;/g, '"') || '';
  const variants = item?.variants ? item?.variants : [];

  const [invisibleHeadingText, imageAlt] = (() => {
    if (!renderProductName || !SEO_CATEGORY_MAP[categoryId]) {
      return [renderProductName, renderProductName];
    }

    const colorLabel = selectedColor?.label || '';
    let headingTextName = renderProductName.trim();
    let altName = headingTextName;

    if (colorLabel) {
      headingTextName = headingTextName.replace(/^([^\s]*\s[^\s]*)/, `$1 ${colorLabel}`);
      altName = altName.replace(/^([^\s]*\s[^\s]*)/, `$1 ${colorLabel} `);
    }

    if (categoryName) {
      altName = altName.replace(/[^\s]*$/, categoryName);
    }

    return [headingTextName, altName];
  })();

  const { finalPrice, crossPrice, currency } = useGetCategoryFinalPriceAndCrossPrice({
    product: item,
  });

  const maybeSales =
    !hideMark && item.product_mark && item.product_mark.indexOf('sale') > -1 ? (
      <div className='absolute -left-0.5 -top-0.5 z-[5] lg_left-[-3px] lg_top-[-3px]'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='53'
          height='32'
          fill='none'
          viewBox='0 0 53 32'
          className='h-auto w-11 lg_w-[53px]'
        >
          <g clipPath='url(#clip0_13126_55737)'>
            <path
              fill='#80583F'
              d='M0 28.316h51.26c.96 0 1.74-.778 1.74-1.737V1.737C53 .777 52.22 0 51.26 0H1.74C.78 0 0 .778 0 1.737z'
            />
            <path
              fill='#80583F'
              d='M1.74 28.313H0v1.736c0-.959.78-1.736 1.74-1.736'
            />
            <path
              fill='#80583F'
              d='M3.058 28.313v3.473H1.74c-.96 0-1.74-.778-1.74-1.737s.78-1.736 1.74-1.736z'
            />
            <path
              fill='#fff'
              d='M16.844 16.244q.312.546.65.962.351.416.78.65t.975.234q.729 0 1.17-.403.442-.403.442-1.027 0-.61-.286-.975a2.05 2.05 0 0 0-.754-.585 8 8 0 0 0-.975-.416 10 10 0 0 1-.754-.299 4.4 4.4 0 0 1-.793-.481q-.377-.3-.624-.728-.234-.429-.234-1.053 0-.676.338-1.209.339-.533.936-.832a2.9 2.9 0 0 1 1.365-.312q.741 0 1.287.286.56.273.95.715.39.43.61.91l-.949.546a3.8 3.8 0 0 0-.429-.663 2.1 2.1 0 0 0-.637-.533q-.363-.208-.897-.208-.74 0-1.079.364a1.12 1.12 0 0 0-.338.793q0 .376.195.702.195.312.65.598.468.273 1.261.546.365.13.767.338.403.208.741.533.351.312.572.767.221.442.221 1.066 0 .598-.234 1.079-.22.48-.624.819a2.8 2.8 0 0 1-.897.52q-.494.182-1.04.182a3.1 3.1 0 0 1-1.378-.312 4 4 0 0 1-1.105-.845 5.3 5.3 0 0 1-.767-1.131zm7.187.91q0 .338.156.585.17.247.455.377.3.117.676.117.482 0 .858-.195.39-.195.611-.546.234-.364.234-.832l.208.78q0 .572-.35.949a2.04 2.04 0 0 1-.846.559q-.493.182-.988.182a2.4 2.4 0 0 1-1.027-.221 1.9 1.9 0 0 1-.754-.663q-.285-.43-.286-1.04 0-.87.611-1.391.624-.533 1.73-.533.65 0 1.078.156.442.143.702.338.26.182.351.299v.611a3.1 3.1 0 0 0-.936-.442 3.6 3.6 0 0 0-1.027-.143q-.507 0-.832.13a1 1 0 0 0-.468.364 1 1 0 0 0-.156.559m-.156-2.756-.507-.767q.338-.248.923-.494.599-.247 1.43-.247.703 0 1.222.221.534.208.82.611.298.403.298.988V19h-1.04v-4.147q0-.312-.104-.507a.78.78 0 0 0-.299-.325 1.3 1.3 0 0 0-.429-.169 2.93 2.93 0 0 0-1.326.052 2.8 2.8 0 0 0-.598.247 5 5 0 0 0-.39.247m5.947-5.538h1.04V19h-1.04zm5.31 10.27q-.831 0-1.482-.39a2.8 2.8 0 0 1-1-1.105q-.352-.702-.352-1.625 0-.936.364-1.638a2.75 2.75 0 0 1 1.04-1.092q.675-.39 1.56-.39.884 0 1.508.377.637.364.975 1.053.338.69.338 1.638 0 .09-.013.195v.117h-5.109v-.884h4.29l-.416.598a1 1 0 0 0 .078-.195 1 1 0 0 0 .04-.234q0-.533-.222-.923a1.53 1.53 0 0 0-.585-.611 1.7 1.7 0 0 0-.884-.221q-.597 0-1.027.26a1.67 1.67 0 0 0-.65.728q-.22.468-.234 1.157 0 .702.221 1.196.222.494.65.754.43.26 1.04.26t1.08-.26q.467-.273.792-.819l.845.533a3.2 3.2 0 0 1-1.183 1.144q-.702.377-1.664.377'
            />
          </g>
          <defs>
            <clipPath id='clip0_13126_55737'>
              <path
                fill='#fff'
                d='M0 0h53v32H0z'
              />
            </clipPath>
          </defs>
        </svg>
      </div>
    ) : null;

  const maybeNewMark =
    !hideMark && item.product_mark && item.product_mark.indexOf('new') > -1 ? (
      <div className='absolute -left-0.5 -top-0.5 z-[5] lg_left-[-3px] lg_top-[-3px]'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='53'
          height='32'
          fill='none'
          viewBox='0 0 53 32'
          className='h-auto w-11 lg_w-[53px]'
        >
          <g clipPath='url(#clip0_11006_94257)'>
            <path
              fill='#80583F'
              d='M0 28.316h51.26c.96 0 1.74-.778 1.74-1.737V1.737C53 .777 52.22 0 51.26 0H1.74C.78 0 0 .778 0 1.737z'
            />
            <path
              fill='#80583F'
              d='M1.74 28.313H0v1.736c0-.959.78-1.736 1.74-1.736'
            />
            <path
              fill='#80583F'
              d='M3.058 28.313v3.473H1.74c-.96 0-1.74-.778-1.74-1.737s.78-1.736 1.74-1.736z'
            />
            <path
              fill='#fff'
              d='M22.222 9.9h1.105v9.555l-6.89-7.228V19h-1.105V9.445l6.89 7.228zm5.44 9.23q-.834 0-1.483-.39a2.8 2.8 0 0 1-1-1.105q-.352-.702-.352-1.625 0-.936.364-1.638a2.75 2.75 0 0 1 1.04-1.092q.675-.39 1.56-.39.884 0 1.508.377.638.364.975 1.053.338.69.338 1.638 0 .09-.013.195v.117H25.49v-.884h4.29l-.416.598a1 1 0 0 0 .078-.195 1 1 0 0 0 .04-.234q0-.533-.222-.923a1.53 1.53 0 0 0-.585-.611 1.7 1.7 0 0 0-.884-.221q-.598 0-1.027.26a1.67 1.67 0 0 0-.65.728q-.22.468-.234 1.157 0 .702.221 1.196.22.494.65.754t1.04.26 1.08-.26q.466-.273.792-.819l.845.533a3.2 3.2 0 0 1-1.183 1.144q-.702.377-1.664.377m3.275-6.11h1.17l1.495 3.887 1.625-4.342 1.742 4.368 1.508-3.913h1.17l-2.6 6.435L35.24 15.1l-1.703 4.355z'
            />
          </g>
          <defs>
            <clipPath id='clip0_11006_94257'>
              <path
                fill='#fff'
                d='M0 0h53v32H0z'
              />
            </clipPath>
          </defs>
        </svg>
      </div>
    ) : null;

  const maybeBestSellerMark =
    !hideMark && item.product_mark && item.product_mark.indexOf('best seller') > -1 ? (
      <div className='absolute -left-0.5 -top-0.5 z-[5] lg_left-[-3px] lg_top-[-3px]'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='83'
          height='32'
          fill='none'
          viewBox='0 0 83 32'
          className='h-auto w-[62px] lg_w-[83px]'
        >
          <g clipPath='url(#clip0_11006_94258)'>
            <path
              fill='#80583F'
              d='M0 28.316h81.26c.96 0 1.74-.778 1.74-1.737V1.737C83 .777 82.22 0 81.26 0H1.74C.78 0 0 .778 0 1.737z'
            />
            <path
              fill='#80583F'
              d='M1.74 28.313H0v1.736c0-.959.78-1.736 1.74-1.736'
            />
            <path
              fill='#80583F'
              d='M3.058 28.313v3.473H1.74c-.96 0-1.74-.778-1.74-1.737s.78-1.736 1.74-1.736z'
            />
            <path
              fill='#fff'
              d='M14.334 14.19v-.611h1.469q.454 0 .819-.156.363-.156.572-.455t.208-.728q0-.65-.455-1.001-.455-.364-1.144-.364h-1.235v7.15h1.365q.585 0 1.027-.208a1.6 1.6 0 0 0 .702-.598q.26-.39.26-.949 0-.415-.143-.741a1.44 1.44 0 0 0-.416-.546 1.65 1.65 0 0 0-.624-.325 2.6 2.6 0 0 0-.806-.117h-1.6v-.611h1.6q.664 0 1.222.143t.975.442q.429.286.663.741t.234 1.079q0 .897-.416 1.495-.403.585-1.105.884-.69.286-1.573.286h-2.47V9.9h2.34q.819 0 1.417.247.611.247.949.754.338.494.338 1.274 0 .637-.338 1.092t-.95.689q-.597.234-1.416.234zm8.627 4.94q-.832 0-1.482-.39a2.8 2.8 0 0 1-1.001-1.105q-.351-.702-.351-1.625 0-.936.364-1.638a2.75 2.75 0 0 1 1.04-1.092q.675-.39 1.56-.39.884 0 1.508.377.637.364.975 1.053.338.69.338 1.638 0 .09-.013.195v.117h-5.11v-.884h4.29l-.415.598a1 1 0 0 0 .078-.195 1 1 0 0 0 .039-.234q0-.533-.221-.923a1.53 1.53 0 0 0-.585-.611 1.7 1.7 0 0 0-.884-.221q-.598 0-1.027.26a1.67 1.67 0 0 0-.65.728q-.222.468-.234 1.157 0 .702.22 1.196.222.494.65.754.43.26 1.04.26.612 0 1.08-.26.468-.273.793-.819l.845.533a3.2 3.2 0 0 1-1.183 1.144q-.702.377-1.664.377m4.623-1.963q.183.273.416.507.234.234.533.377.313.13.676.13.443 0 .69-.195a.68.68 0 0 0 .26-.559.7.7 0 0 0-.209-.52 1.5 1.5 0 0 0-.507-.364 7 7 0 0 0-.663-.286 9 9 0 0 1-.793-.364 2.3 2.3 0 0 1-.663-.559q-.26-.35-.26-.871 0-.533.273-.884.287-.35.728-.52a2.67 2.67 0 0 1 2.587.403q.326.26.507.598l-.832.533a2 2 0 0 0-.624-.559 1.57 1.57 0 0 0-.832-.221q-.324 0-.559.143a.48.48 0 0 0-.234.442q0 .234.182.416.183.182.468.325.286.142.598.273.52.208.95.455.428.234.675.572.26.338.26.871 0 .78-.572 1.3-.558.52-1.482.52-.597 0-1.079-.208a2.8 2.8 0 0 1-.819-.559 2.7 2.7 0 0 1-.494-.689zm4.087-4.147h2.99v.975h-2.99zm.974-2.08h1.04V19h-1.04zm7.359 5.304q.312.546.65.962.351.416.78.65t.975.234q.727 0 1.17-.403t.442-1.027q0-.61-.286-.975a2.05 2.05 0 0 0-.754-.585 8 8 0 0 0-.975-.416 10 10 0 0 1-.754-.299 4.4 4.4 0 0 1-.793-.481q-.377-.3-.624-.728-.234-.429-.234-1.053 0-.676.338-1.209.337-.533.936-.832a2.9 2.9 0 0 1 1.365-.312q.741 0 1.287.286.56.273.949.715.39.43.611.91l-.949.546a3.8 3.8 0 0 0-.429-.663 2.1 2.1 0 0 0-.637-.533q-.365-.208-.897-.208-.741 0-1.079.364a1.11 1.11 0 0 0-.338.793q0 .376.195.702.195.312.65.598.468.273 1.261.546.364.13.767.338t.741.533q.351.312.572.767.22.442.221 1.066 0 .598-.234 1.079a2.3 2.3 0 0 1-.624.819 2.8 2.8 0 0 1-.897.52q-.494.182-1.04.182a3.1 3.1 0 0 1-1.378-.312 4 4 0 0 1-1.105-.845 5.3 5.3 0 0 1-.767-1.131zm9.033 2.886q-.833 0-1.482-.39a2.8 2.8 0 0 1-1.001-1.105q-.351-.702-.351-1.625 0-.936.364-1.638a2.75 2.75 0 0 1 1.04-1.092q.675-.39 1.56-.39.884 0 1.508.377.637.364.975 1.053.338.69.338 1.638 0 .09-.013.195v.117h-5.109v-.884h4.29l-.416.598a1 1 0 0 0 .078-.195 1 1 0 0 0 .039-.234q0-.533-.221-.923a1.53 1.53 0 0 0-.585-.611 1.7 1.7 0 0 0-.884-.221q-.598 0-1.027.26a1.67 1.67 0 0 0-.65.728q-.22.468-.234 1.157 0 .702.221 1.196.22.494.65.754t1.04.26 1.079-.26q.468-.273.793-.819l.845.533a3.2 3.2 0 0 1-1.183 1.144q-.702.377-1.664.377m4.377-10.27h1.04V19h-1.04zm2.996 0h1.04V19h-1.04zm5.31 10.27q-.833 0-1.482-.39a2.8 2.8 0 0 1-1.001-1.105q-.351-.702-.351-1.625 0-.936.364-1.638a2.75 2.75 0 0 1 1.04-1.092q.675-.39 1.56-.39.884 0 1.508.377.637.364.975 1.053.338.69.338 1.638 0 .09-.013.195v.117h-5.11v-.884h4.29l-.415.598a1 1 0 0 0 .078-.195 1 1 0 0 0 .039-.234q0-.533-.221-.923a1.53 1.53 0 0 0-.585-.611 1.7 1.7 0 0 0-.884-.221q-.598 0-1.027.26a1.67 1.67 0 0 0-.65.728q-.222.468-.234 1.157 0 .702.22 1.196.222.494.65.754.43.26 1.04.26.612 0 1.08-.26.468-.273.793-.819l.845.533a3.2 3.2 0 0 1-1.183 1.144q-.702.377-1.664.377m5.416-6.11V19h-1.04v-5.98zm1.807 1.144a1.6 1.6 0 0 0-.377-.221 1 1 0 0 0-.429-.078q-.39 0-.61.195-.222.195-.313.533a3.4 3.4 0 0 0-.078.767h-.468q0-.715.247-1.261.26-.559.663-.884t.82-.325q.324 0 .597.091.285.078.52.325z'
            />
          </g>
          <defs>
            <clipPath id='clip0_11006_94258'>
              <path
                fill='#fff'
                d='M0 0h83v32H0z'
              />
            </clipPath>
          </defs>
        </svg>
      </div>
    ) : null;

  const maybeVideoMark =
    !hideMark && item.video_url ? (
      <div className='absolute right-0 top-0 z-[5]'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='29'
          height='28'
          fill='none'
          viewBox='0 0 29 28'
          className='h-auto w-5 lg_w-[29px]'
        >
          <g
            fillRule='evenodd'
            clipPath='url(#clip0_11006_94289)'
            clipRule='evenodd'
          >
            <path
              fill='#80583F'
              d='M29 28C12.984 28 0 15.464 0 0h29z'
            />
            <path
              fill='#F8F3EF'
              d='M11.254 7h9.972c.143 0 .255.108.255.246v1.591c0 .079.03.148.091.187a.24.24 0 0 0 .214.04l2.646-.65c.081-.019.152 0 .213.04.061.05.092.118.092.187v4.735c0 .079-.03.148-.092.187a.24.24 0 0 1-.213.04l-2.646-.65a.27.27 0 0 0-.214.04.24.24 0 0 0-.091.187v1.591a.25.25 0 0 1-.255.246h-9.972A.25.25 0 0 1 11 14.77V7.246c0-.138.112-.246.254-.246m4.07 1.876s-.091-.029-.132 0a.12.12 0 0 0-.07.109v4.067s.02.088.07.108c.041.02.092.02.133 0l3.215-2.034s.061-.059.061-.098-.02-.079-.061-.098l-3.215-2.034z'
            />
          </g>
          <defs>
            <clipPath id='clip0_11006_94289'>
              <path
                fill='#fff'
                d='M0 0h29v28H0z'
              />
            </clipPath>
          </defs>
        </svg>
      </div>
    ) : null;

  const maybeOutOfStock =
    item.stock_status === 'OUT_OF_STOCK' ? (
      <div className='mt-1 inline-block w-[100px] rounded-[5px] border-1 border-solid border-80583F  text-center text-sm leading-6 text-80583F  '>
        Out of stock
      </div>
    ) : null;

  const maybefreeShipping = freeShipping ? (
    <div className='mt-1 inline-block w-[100px] rounded-[50px] bg-E7DED5 text-center text-sm leading-6 text-80583F '>
      Free Shipping
    </div>
  ) : null;

  const imageTop = isMobile ? (
    <Link
      href={renderProductLink}
      className={classnameMerge('relative mb-2.5 block lg_mb-5', classes.imageLink)}
      style={{
        paddingBottom: `${(470 / 320) * 100}%`,
      }}
    >
      {versionedBaseImage ? (
        <Image
          alt={imageAlt}
          placeholder={NEXT_IMAGE_PLACEHOLDER}
          width={320}
          height={470}
          src={versionedBaseImage}
          loader={({ src }) => src}
          unoptimized
          className='absolute left-0 top-0 h-auto w-full object-cover'
        />
      ) : null}
    </Link>
  ) : (
    <Link
      onClick={handleLinkClick}
      href={renderProductLink}
      className={classnameMerge('group relative mb-2.5 block lg_mb-5', classes.imageLink)}
      style={{
        paddingBottom: `${(470 / 320) * 100}%`,
      }}
    >
      {versionedBaseImage ? (
        <Image
          alt={imageAlt}
          placeholder={NEXT_IMAGE_PLACEHOLDER}
          width={320}
          height={470}
          src={versionedBaseImage}
          loader={({ src }) => src}
          unoptimized
          className={clsx(
            'absolute left-0 top-0 z-[1] h-auto w-full object-cover opacity-100 transition-opacity duration-4xl',
            versionedHoverImage ? 'group-hover_opacity-0' : '',
          )}
        />
      ) : null}
      {versionedHoverImage ? (
        <Image
          alt={imageAlt}
          placeholder={NEXT_IMAGE_PLACEHOLDER}
          width={320}
          height={470}
          src={versionedHoverImage}
          loader={({ src }) => src}
          unoptimized
          className='absolute left-0 top-0 z-[2] h-auto w-full object-cover opacity-0 transition-opacity duration-4xl group-hover_opacity-100'
        />
      ) : null}
    </Link>
  );

  return (
    <div className='relative'>
      {imageTop}
      {showCustomizeOptions && colorOptions?.length > 0 && (
        <CustomizeOptions
          allColorCount={allColorCount}
          colorOptions={colorOptions}
          variants={variants}
          ampProtOption={ampProtOption}
          selectedShowColorIndex={selectedShowColorIndex}
          setSelectedShowColorIndex={setSelectedShowColorIndex}
          generateLinkByColorLabel={generateLinkByColorLabel}
        />
      )}
      {selectedColor && SEO_CATEGORY_MAP[categoryId] ? (
        <p className='mt-[5px] text-xs leading-[14px] text-80583F lg_text-[13px] lg_leading-4 lg_-tracking-tighter'>
          {selectedColor.label}
        </p>
      ) : null}
      {hideProductName ? null : (
        <Link
          onClick={handleLinkClick}
          href={renderProductLink}
          className={classnameMerge(
            'm-0.5 block text-sm font-medium leading-4 lg_text-base lg_font-normal lg_leading-6',
            classes.name,
          )}
          target='_blank'
        >
          {inCategoryPage ? <h2 className='hidden'>{invisibleHeadingText}</h2> : null}
          {renderProductName}
        </Link>
      )}
      <div className={clsx('mt-0.5 flex flex-col-reverse lg_flex-col', classes.priceWrapper)}>
        <div className={classnameMerge('text-sm leading-5 lg_text-lg lg_leading-6', classes.price)}>
          <Price
            specialPrice={crossPrice}
            value={finalPrice}
            currencyCode={currency}
            classes={{
              specialPrice: 'text-xs lg_text-sm lg_leading-5 ml-[7px] line-through text-[#ABA6A0]',
            }}
          />
        </div>
        {showRating && showReview && item.review_count ? (
          <div className='flex items-center gap-x-[5px]'>
            <Rating
              rating={rating_summary}
              disabled
            />
            <span className='text-xs leading-[18px]'>({item.review_count})</span>
          </div>
        ) : null}
      </div>
      {maybeOutOfStock}
      {maybeNewMark}
      {maybeBestSellerMark}
      {maybeVideoMark}
      {maybeSales}
      {item.fabrics_mark ? (
        <div className='mt-2 font-CeraPro text-xs leading-4 text-[#5e5047]'>
          <span className='inline-block rounded-[50px] bg-E7DED5 px-2 py-0.5'> Multiple Fabrics Available</span>
        </div>
      ) : null}
      {maybefreeShipping}
    </div>
  );
};

export default GalleryItem;
